<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Bot } from 'lucide-vue-next'
import Header from '@/components/layout/Header.vue'
import Footer from '@/components/layout/Footer.vue'
import MobileBottomNav from '@/components/layout/MobileBottomNav.vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import AiAssistantModal from '@/components/layout/AiAssistantModal.vue'
import BrandSplashScreen from '@/components/common/BrandSplashScreen.vue'
import { useFeatureStore } from '@/stores/feature'
import { useI18n } from '@/composables/useI18n'

const route = useRoute()
const router = useRouter()

// Thin sun-coloured progress line while a route (and its lazy chunk) loads.
// It only appears for navigations slower than ~150 ms, so quick hops stay clean.
const navState = ref<'idle' | 'loading' | 'done'>('idle')
let navShowTimer: ReturnType<typeof setTimeout> | null = null
let navDoneTimer: ReturnType<typeof setTimeout> | null = null
router.beforeEach((to, from) => {
  if (to.path === from.path) return
  if (navShowTimer) clearTimeout(navShowTimer)
  if (navDoneTimer) clearTimeout(navDoneTimer)
  navShowTimer = setTimeout(() => { navState.value = 'loading' }, 150)
})
function finishNav() {
  if (navShowTimer) { clearTimeout(navShowTimer); navShowTimer = null }
  if (navState.value !== 'loading') return
  navState.value = 'done'
  navDoneTimer = setTimeout(() => { navState.value = 'idle' }, 450)
}
router.afterEach(finishNav)
router.onError(finishNav)
const { t } = useI18n()
const featureStore = useFeatureStore()
const showAiAssistant = ref(false)

// The auction detail page gets its own sticky bid bar on mobile instead —
// two competing fixed bottom bars would fight for the same thumb real estate.
const showMobileNav = computed(() => route.name !== 'AuctionDetail')

// Auth screens are a single narrow card with no room to spare, so neither the
// floating pill nor the docked assistant row belongs there — the pill would
// land straight on the consent copy / trust note, and the row would push the
// sign-in card further from the fold.
const AUTH_ROUTE_NAMES = ['Login', 'Register', 'ForgotPassword', 'ResetPassword', 'VerifyEmail']
const isAuthRoute = computed(() => AUTH_ROUTE_NAMES.includes(String(route.name ?? '')))

// The floating pill has to yield once the footer scrolls into view, otherwise
// it sits on top of the legal links and makes them unclickable.
const footerSentinel = ref<HTMLElement | null>(null)
const isFooterInView = ref(false)
let documentResizeObserver: ResizeObserver | null = null

const isAiPillHidden = computed(() => isFooterInView.value || isAuthRoute.value)

// A little slack so the pill clears out just before it would touch the footer.
const FOOTER_CLEARANCE = 24

function syncFooterProximity() {
  const el = footerSentinel.value
  if (!el) return
  isFooterInView.value = el.getBoundingClientRect().top - FOOTER_CLEARANCE <= window.innerHeight
}

onMounted(() => {
  featureStore.fetchFeaturesConfig()
  featureStore.fetchBanksConfig()

  syncFooterProximity()
  window.addEventListener('scroll', syncFooterProximity, { passive: true })
  window.addEventListener('resize', syncFooterProximity)
  // Async page data changes the document height without firing a scroll event.
  if (typeof ResizeObserver !== 'undefined') {
    documentResizeObserver = new ResizeObserver(syncFooterProximity)
    documentResizeObserver.observe(document.documentElement)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', syncFooterProximity)
  window.removeEventListener('resize', syncFooterProximity)
  documentResizeObserver?.disconnect()
  documentResizeObserver = null
})

watch(() => route.fullPath, () => nextTick(syncFooterProximity))
</script>

<template>
  <div class="min-h-screen w-full bg-[#f1f3f6] font-sans antialiased selection:bg-primary selection:text-onPrimary">
    <!-- Centered Boxed App Shell (max 1440px, framed on ultrawide/large screens) -->
    <div class="min-h-screen w-full max-w-[1440px] mx-auto flex flex-col bg-background text-text-primary shadow-2xl shadow-black/5 border-x border-border/40 relative">
      <div aria-hidden="true" class="nav-progress" :class="`nav-progress--${navState}`" />

      <!-- Fixed Header (constrained within 1440px) -->
      <Header />

      <!-- Main Content. At least a screen tall: while a page's chunk or data
           loads, the footer would otherwise sit mid-screen and then be shoved
           down (a layout shift on every first visit). -->
      <main id="main-content" role="main" class="flex-1 min-h-screen w-full max-w-full overflow-x-clip">
        <RouterView v-slot="{ Component }">
          <!-- out-in: the old page leaves first (fast), so two pages never
               share the flow and the layout does not jump mid-transition. -->
          <transition name="page" mode="out-in" :duration="{ enter: 420, leave: 120 }">
            <component :is="Component" />
          </transition>
        </RouterView>
      </main>

      <!-- Mobile entry point for the AI assistant. At 375px the content column
           runs from edge to edge, so nothing reserves a right-hand gutter and a
           floating pill would hit-test straight onto live copy on every route.
           On small screens the assistant is docked into the page flow instead,
           just above the footer, where it can never cover anything. -->
      <div
        v-if="featureStore.isAiAssistantEnabled && !isAuthRoute"
        class="w-full px-4 pb-8 pt-2 lg:hidden"
      >
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-card border border-border bg-surface p-4 text-left transition hover:bg-accent active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          @click="showAiAssistant = true"
          :title="t('modules.aiAssistant.title')"
          :aria-label="t('modules.aiAssistant.floatingButton')"
        >
          <span class="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-onPrimary">
            <Bot class="h-5 w-5" />
            <span class="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-surface"></span>
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate font-display text-[15px] font-semibold text-text-primary">{{ t('modules.aiAssistant.floatingButton') }}</span>
            <span class="block truncate text-[11px] text-text-muted">{{ t('modules.aiAssistant.subtitle') }}</span>
          </span>
        </button>
      </div>

      <!-- Footer (wrapped so the floating assistant can tell when it is on screen) -->
      <div ref="footerSentinel" class="w-full">
        <Footer />
      </div>

      <!-- Reserves the height of whichever fixed bottom bar the route shows on
           mobile — the global nav, or the auction page's own bid bar — so the
           footer's legal row is never buried. Painted in the footer's own ink so
           the strip reads as part of it. -->
      <div
        class="h-[calc(4.25rem_+_env(safe-area-inset-bottom))] w-full shrink-0 bg-ink lg:hidden"
        aria-hidden="true"
      ></div>

      <!-- Mobile Bottom Navigation (not on auction detail) -->
      <MobileBottomNav v-if="showMobileNav" />

      <!-- Floating AI Shopping Assistant Button (controlled via Admin Feature Flags).
           Desktop only: positioned inside the 1440px frame on ultrawide screens. -->
      <button
        v-if="featureStore.isAiAssistantEnabled"
        type="button"
        class="fixed bottom-7 z-40 hidden cursor-pointer items-center gap-2 rounded-full border border-ink/10 bg-primary px-4 py-3 text-xs font-bold text-onPrimary shadow-[0_10px_28px_-8px_rgb(var(--color-primary)/0.7)] transition duration-300 hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:inline-flex"
        :style="{ right: 'max(1.25rem, calc((100vw - 1440px) / 2 + 1.25rem))' }"
        :class="isAiPillHidden ? 'pointer-events-none translate-y-3 opacity-0' : 'translate-y-0 opacity-100'"
        :tabindex="isAiPillHidden ? -1 : undefined"
        :aria-hidden="isAiPillHidden ? 'true' : undefined"
        @click="showAiAssistant = true"
        :title="t('modules.aiAssistant.title')"
        :aria-label="t('modules.aiAssistant.floatingButton')"
      >
        <span class="relative inline-flex">
          <Bot class="h-5 w-5" />
          <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-success ring-2 ring-primary"></span>
        </span>
        <span class="tracking-tight">{{ t('modules.aiAssistant.floatingButton') }}</span>
      </button>

      <!-- AI Assistant Modal -->
      <AiAssistantModal v-if="featureStore.isAiAssistantEnabled" v-model="showAiAssistant" />

      <!-- Global Toast Container -->
      <ToastContainer />

      <!-- Brief loading spinner shown on first visit (Admin Configurable) -->
      <BrandSplashScreen />
    </div>
  </div>
</template>

<style>
/* `overflow-x: hidden` turns a box into a scroll container (the used value of
   overflow-y becomes `auto`), which silently kills every `position: sticky`
   sidebar nested inside it — the scrollport exists but never scrolls. The base
   layer in src/styles/tailwind.css sets it with `!important` on html, body and
   #app, so the app shell overrides it here with `clip`, which performs exactly
   the same horizontal clipping without ever creating a scrollport. Selectors
   are deliberately more specific than the base rules so they win. */
:root:root,
:root body,
body > #app {
  overflow-x: clip !important;
}
</style>

<style scoped>
/* Page change: the old page fades out quickly, the new one rises in with a
   soft ease-out. Reduced motion: opacity only. */
.page-leave-active {
  transition: opacity 0.12s ease-in;
}
.page-leave-to {
  opacity: 0;
}
.page-enter-active {
  transition: opacity 0.32s ease-out, transform 0.42s cubic-bezier(0.16, 1, 0.3, 1);
}
.page-enter-from {
  opacity: 0;
  transform: translate3d(0, 14px, 0);
}
@media (prefers-reduced-motion: reduce) {
  .page-enter-active { transition: opacity 0.2s ease-out; }
  .page-enter-from { transform: none; }
}

.nav-progress {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 70;
  height: 3px;
  width: 0;
  opacity: 0;
  background: rgb(var(--color-sun));
  box-shadow: 0 0 10px rgb(var(--color-sun) / 0.7);
  pointer-events: none;
}
.nav-progress--loading {
  opacity: 1;
  width: 80%;
  transition: width 2.5s cubic-bezier(0.1, 0.6, 0.2, 1), opacity 0.1s;
}
.nav-progress--done {
  opacity: 0;
  width: 100%;
  transition: width 0.2s ease-out, opacity 0.3s ease 0.15s;
}
</style>
